import type { AdminSettings, CallDetail, CallEvent, CallListItem, ConnectionAttempt, MetricsResponse, Voice, VoicesResponse } from './types'

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = new URL(path, window.location.origin)
  const response = await fetch(url.toString(), {
    ...init,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(init?.headers || {}) },
  })

  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(body?.detail || `Request failed: ${response.status}`)
  }

  return response.json() as Promise<T>
}

export function getMetrics(params?: { period?: string; day?: string }) {
  const query = new URLSearchParams()
  if (params?.period) query.set('period', params.period)
  if (params?.day) query.set('day', params.day)
  const suffix = query.toString() ? `?${query}` : ''
  return requestJson<MetricsResponse>(`/api/admin/metrics${suffix}`)
}

export function listCalls(params: {
  status?: string
  lang?: string
  search?: string
  limit?: number
  offset?: number
}) {
  const query = new URLSearchParams()
  query.set('limit', String(params.limit ?? 50))
  query.set('offset', String(params.offset ?? 0))
  if (params.status) query.set('status', params.status)
  if (params.lang) query.set('lang', params.lang)
  if (params.search) query.set('search', params.search)
  return requestJson<{ configured: boolean; items: CallListItem[]; total: number }>(`/api/admin/calls?${query}`)
}

export function listConnectionAttempts(params: {
  status?: string
  problemOnly?: boolean
  limit?: number
}) {
  const query = new URLSearchParams()
  query.set('limit', String(params.limit ?? 50))
  if (params.status) query.set('status', params.status)
  if (params.problemOnly) query.set('problem_only', 'true')
  return requestJson<{ configured: boolean; items: ConnectionAttempt[] }>(`/api/admin/connection-attempts?${query}`)
}

export function getCall(callId: string) {
  return requestJson<CallDetail>(`/api/admin/calls/${callId}`)
}

export function getCallEvents(callId: string) {
  return requestJson<{ items: CallEvent[] }>(`/api/admin/calls/${callId}/events`)
}

export function deleteCallRecordings(callId: string) {
  return requestJson<{ deleted_tracks: string[] }>(`/api/admin/calls/${callId}/recordings`, {
    method: 'DELETE',
  })
}

export function getTranscriptUrl(callId: string) {
  return `/api/admin/calls/${callId}/transcript`
}

export function getSettings() {
  return requestJson<{ configured: boolean; settings: AdminSettings }>('/api/admin/settings')
}

export function updateSettings(settings: Partial<AdminSettings>) {
  return requestJson<{ configured: boolean; settings: AdminSettings }>('/api/admin/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  })
}

export function getVoices() {
  return requestJson<VoicesResponse>('/api/admin/voices')
}

export function updateVoices(voices: Voice[]) {
  return requestJson<VoicesResponse>('/api/admin/voices', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ voices }),
  })
}

export function setDefaultVoice(id: string) {
  return requestJson<VoicesResponse>('/api/admin/voices/default', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  })
}
